import { ChevronDown, FlaskConical, LogOut, Settings, User, Wifi, WifiHigh, WifiLow, WifiOff } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { useGravatar } from 'use-gravatar';

import {
  cn,
  IconButton,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui';
import { useAuth } from '@/context/appContext';
import { TABS_MAPPING } from '@/context/FeatureContext';
import { useSocketTelemetryData } from '@/socketio/SocketIOService';

import { FeatureFlagTab } from './FeatureFlagTab';
import ProfileTab from './ProfileTab';

/** Signal bars for the socket round trip: full and green when fast, fewer and warmer as it slows. */
const NetworkIcon = () => {
  const networkStatus = useSocketTelemetryData();
  const goodNetwork = 500;
  const badNetwork = 2000;
  const rtt = networkStatus.rttLatest;

  // rtt null with samples means connected but no aged sample yet; treat that as green.
  // rtt 0 is the socket telemetry sentinel for a failed ping (see getSocketTelemetryData).
  const status = !networkStatus.rttTotal
    ? { Icon: WifiOff, className: 'text-ink-subtle', label: 'Live updates not connected' }
    : rtt == null
      ? { Icon: Wifi, className: 'text-positive', label: 'Live updates connected' }
      : rtt === 0
        ? { Icon: WifiOff, className: 'text-critical', label: 'Live updates disconnected' }
        : rtt <= goodNetwork
          ? { Icon: Wifi, className: 'text-positive', label: `Connection good (${Math.round(rtt)} ms)` }
          : rtt > badNetwork
            ? { Icon: WifiLow, className: 'text-critical', label: `Connection poor (${Math.round(rtt)} ms)` }
            : { Icon: WifiHigh, className: 'text-caution', label: `Connection slow (${Math.round(rtt)} ms)` };

  return (
    <span role="img" aria-label={status.label} title={status.label} className="inline-flex">
      <status.Icon aria-hidden="true" className={cn('size-5', status.className)} />
    </span>
  );
};

const Profile = () => {
  const { logout, authInfo } = useAuth();
  const [open, setOpen] = useState(false);
  const avatarUrl = useGravatar(authInfo?.email || '', 32);
  const initials =
    (authInfo?.email ?? '')
      .replace(/[^a-zA-Z]/g, '')
      .slice(0, 2)
      .toUpperCase() || '?';

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          // Starts with the visible initials so speech users can say what they see.
          aria-label={`${initials}, open profile`}
          className="flex shrink-0 cursor-pointer items-center gap-2 rounded-full p-0.5 text-sm font-medium text-ink transition-colors hover:bg-canvas focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand sm:p-1 sm:pl-2"
        >
          {/* Phones show just the avatar; connection details stay in the Profile tab. */}
          <span className="hidden sm:inline-flex">
            <NetworkIcon />
          </span>
          <span
            title={authInfo?.email}
            className="flex size-9 items-center justify-center rounded-full bg-brand-subtle text-sm font-semibold uppercase leading-none text-brand"
          >
            {initials}
          </span>
          <ChevronDown aria-hidden="true" className="hidden size-4 text-ink-muted sm:block" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-profile overflow-hidden p-0"
        // Focus the panel itself; focusing the first control would pop its tooltip open.
        tabIndex={-1}
        onOpenAutoFocus={event => {
          event.preventDefault();
          (event.currentTarget as HTMLElement).focus();
        }}
      >
        <div className="flex items-center justify-between gap-3 border-b border-line p-4">
          <div className="flex min-w-0 items-center gap-3">
            <img alt="profile user" src={avatarUrl} className="size-8 rounded-full bg-brand-subtle object-cover" />
            <div className="min-w-0">
              <p className="m-0 truncate text-sm font-semibold text-ink">{authInfo?.email}</p>
              {authInfo?.review_level && (
                <p className="m-0 mt-0.5 capitalize text-xs text-ink-muted">{authInfo.review_level}</p>
              )}
            </div>
          </div>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <IconButton label="Logout" variant="ghost" onClick={handleLogout}>
                  <LogOut className="size-4" />
                </IconButton>
              </TooltipTrigger>
              <TooltipContent>Logout</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        <Tabs defaultValue="profile">
          <TabsList className="grid h-auto w-full grid-cols-3 gap-1 rounded-none border-b border-line bg-canvas p-1.5">
            <TabsTrigger value="profile" className="gap-1.5">
              <User className="size-4" /> Profile
            </TabsTrigger>
            <TabsTrigger value="settings" className="gap-1.5">
              <Settings className="size-4" /> Settings
            </TabsTrigger>
            <TabsTrigger value="debug" className="gap-1.5">
              <FlaskConical className="size-4" /> Debug
            </TabsTrigger>
          </TabsList>
          <TabsContent value="profile" className="m-0">
            <ProfileTab handleLogout={handleLogout} />
          </TabsContent>
          <TabsContent value="settings" className="m-0">
            <FeatureFlagTab tab={TABS_MAPPING.settings} />
          </TabsContent>
          <TabsContent value="debug" className="m-0">
            <FeatureFlagTab tab={TABS_MAPPING.features}>
              <Link
                to="/food/engine"
                onClick={() => setOpen(false)}
                className="block cursor-pointer px-4 py-3 text-sm text-ink hover:bg-canvas focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand"
              >
                Food search (Elastic, Engine)
              </Link>
            </FeatureFlagTab>
          </TabsContent>
        </Tabs>
      </PopoverContent>
    </Popover>
  );
};

export default Profile;
